import { useEffect, useState } from "react";

interface TocProps {
  items: { id: string; heading: string }[];
}

export default function Toc({ items }: TocProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (sections.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: [0, 1] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items]);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <>
      <div className="mb-6 rounded-lg border border-background-200 bg-background-100/70 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-sm text-foreground-800"
        >
          <span className="flex items-center gap-2">
            <i className="ri-list-check-2 text-primary-500" />
            本文目录
          </span>
          <i className={`ri-arrow-down-s-line transition-transform ${mobileOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileOpen ? (
          <ol className="animate-fade-in space-y-1 border-t border-background-200 px-4 py-3">
            {items.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="flex w-full cursor-pointer items-start gap-2 py-1.5 text-left text-xs text-foreground-600"
                >
                  <span className="font-editorial text-foreground-400">0{index + 1}</span>
                  {item.heading}
                </button>
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      <nav aria-label="文章目录" className="hidden lg:block">
        <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="mb-3 flex items-center gap-2 border-b border-background-200 pb-2.5 font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
            <i className="ri-list-check-2 text-primary-500" />
            Contents / 目录
          </p>
          <ol className="space-y-1">
            {items.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className={`flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-left text-xs leading-relaxed transition-colors ${
                    activeId === item.id
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-500 hover:bg-background-100 hover:text-foreground-800"
                  }`}
                >
                  <span className="font-editorial text-[10px] text-foreground-400">0{index + 1}</span>
                  <span className="min-w-0">{item.heading}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
}